import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import swal from 'sweetalert';
import AuthContext, { User, SignInCredentials } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import eventEmitter from '../utils/eventEmitter';

interface AuthProviderProps {
  children: ReactNode;
}

interface AuthState {
  user: User | null;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation(); // <--- adicionado
  const [data, setData] = useState<AuthState>({ user: null });
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  /**
   * Carrega os dados do usuário (GET /users/me).
   * Se der sucesso, atualiza user; caso contrário, signOut().
   */
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/users/me');
      setData({ user: response.data });
      setAuthenticated(true);
    } catch (error) {
      console.error('Erro ao obter /users/me:', error);
      signOut(); // se 401 ou erro fatal, desloga
    }
  }, []);

  /**
   * Primeira verificação de autenticação ao montar.
   */
  useEffect(() => {
    (async () => {
      setLoading(true);
      await refreshUser();
      setLoading(false);
    })();

    // Listener global para forçar logout em outros contextos
    const handleLogout = () => signOut();
    eventEmitter.on('logout', handleLogout);

    return () => {
      eventEmitter.off('logout', handleLogout);
    };
  }, [refreshUser]);

  /**
   * Faz login: POST /auth/.
   * Após sucesso, redireciona para redirectUrl ou /perfil.
   */
  const signIn = useCallback(
    async ({ email, password }: SignInCredentials, redirectUrl?: string) => {
      try {
        const response = await api.post('/auth/', { email, password });
        const { user } = response.data;
        
        setData({ user });
        setAuthenticated(true);
  
        if (redirectUrl) {
          if (redirectUrl.startsWith('http')) {
            // Remove "/login" do final da URL externa, se presente
            const sanitizedRedirectUrl = redirectUrl.replace(/\/login$/, '');
            window.location.href = sanitizedRedirectUrl;
          } else {
            navigate(redirectUrl); // Navegação interna
          }
        } else {
          navigate('/perfil'); // Navegação padrão
        }
      } catch (err: any) {
        setAuthenticated(false);
  
        if (err.response) {
          if (err.response.status === 401) {
            swal('Ooops! 401!', 'E-mail ou senha incorretos.', 'error');
          } else {
            swal(`Erro ${err.response.status}`, 'Falha no login.', 'error');
          }
        } else {
          swal('Ooops!', `${err}`, 'error');
        }
      }
    },
    [navigate],
  );  

  /**
   * Faz logout: POST /auth/logout/.
   * Limpa user no contexto e navega para /login mantendo qualquer query atual.
   */
  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout/');
    } catch (err) {
      console.error('Erro ao fazer logout no servidor:', err);
    } finally {
      setData({ user: null });
      setAuthenticated(false);
      // Preserve a query string atual (que pode conter "?redirect=...")
      navigate('/login' + location.search);
    }
  }, [navigate, location]);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        authenticated,
        loading,
        signIn,
        signOut,
        refreshUserToken: refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;